<template>
    <app-base>
        <app-layout v-if="viewer !== undefined">
            <slot />
        </app-layout>
    </app-base>
</template>

<script setup lang="ts">
import AppLayout from '~/components/Layouts/ProfileLayout/AppLayout.vue'
import { provideViewer, useViewer } from '~/composables/use-login'
import { useRouter } from 'vue-router'
import { useRouteLink } from '~/composables/routing'
import { computed, onBeforeUnmount, ref } from 'vue'
import type { ActiveFacility } from '~/composables/use-active-facility'
import { provideActiveFacility } from '~/composables/use-active-facility'
import AppBase from '~/components/Layouts/AppBase.vue'
import { navigateTo } from '#app/composables/router'
import { useHead } from '@unhead/vue'
import { createLayout } from '~/components/Layouts/ProfileLayout/composables/layout'
import { useShowAlert } from '~/composables/use-show-alert'
import { useLayoutMode } from '#imports'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const { isMobileApp } = useLayoutMode()
const viewer = useViewer()
const showAlert = useShowAlert()
const { t } = useI18n({ useScope: 'global' })

if (isMobileApp.value) {
    if (viewer.value === undefined) {
        showAlert(t('profile.viewerUndefined'), t('profile.mobileApp'))
    }
} else {
    const { orgSignInLink } = useRouteLink()
    await provideViewer()
    if (viewer.value === undefined) {
        // Redirect to org login page
        await navigateTo(orgSignInLink(router.currentRoute.value.fullPath))
    }
}

if (viewer.value !== undefined) {
    const homeFacility = ref<ActiveFacility>(viewer.value.homeFacility)
    provideActiveFacility(homeFacility)

    const unmount = ref(false)
    const headStyle = computed(() => {
        if (unmount.value) {
            return []
        }

        return homeFacility.value.branding.styleString
    })

    const headLinks = computed(() => {
        return homeFacility.value.organization.branding.headLinks
    })

    useHead({
        style: headStyle,
        link: headLinks
    })

    onBeforeUnmount(() => {
        // Clear styles when leaving the component
        unmount.value = true
    })
}

createLayout()
</script>
